import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

// Cast import.meta to any to align with other services and avoid TS complaints
const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

export interface FetchRun {
  id: number;
  status: string;
  triggeredBy: string | null;
  startedAt: string | null;
  endedAt: string | null;
  notes: string | null;
  errorMessage?: string | null;
  requestPayload: any | null;
  totalQuoteSources?: number | null;
  processedQuoteSources?: number | null;
  successCount?: number | null;
  failedCount?: number | null;
  skippedCount?: number | null;
  currentQuoteSourceId?: number | null;
  currentQuoteSourceSymbol?: string | null;
  estimatedCompletionAt?: string | null;
  totalPricePointsSaved?: number | null;
}

export interface FetchRunItem {
  id: number;
  quoteSourceId: number;
  formulaId: number | null;
  sourceSymbol: string;
  status: string;
  errorMessage: string | null;
  rowsInserted: number;
  fetchWindowStart: string | null;
  fetchWindowEnd: string | null;
  latencyMs: number | null;
}

export interface CreateFetchJobRequest {
  startDate?: string;
  overwrite?: boolean;
  lookbackDays?: number;
  quoteSourceIds?: number[];
}

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;
    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      resolve(null);
    }, timeoutMs);
    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function getFetchRuns(): Promise<FetchRun[]> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/market-data/fetch-jobs`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch runs');
  }

  const data = await response.json();
  return data.runs;
}

export async function createFetchJob(payload: CreateFetchJobRequest): Promise<FetchRun> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/market-data/fetch-jobs`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create fetch job');
  }

  const data = await response.json();
  return data.run;
}

export async function deleteFetchJob(id: number): Promise<void> {
  const response = await fetch(`${API_URL}/api/v2/market-data/fetch-jobs/${id}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to delete/cancel fetch job' }));
    throw new Error(errorData.error || 'Failed to delete/cancel fetch job');
  }
}

export async function getFetchRun(id: number): Promise<FetchRun> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/market-data/fetch-jobs/${id}`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch run');
  }

  const data = await response.json();
  return data.run;
}

export async function getFetchRunItems(runId: number): Promise<FetchRunItem[]> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/api/v2/market-data/fetch-jobs/${runId}/items`, {
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch run items');
  }

  const data = await response.json();
  return data.items;
}
